import { stripeApi } from './stripeClient';

/**
 * Stripe Health Check Service
 *
 * Validates that frontend and backend Stripe configurations match
 * (both using test mode or both using live mode).
 *
 * Caches results for 5 minutes to reduce API overhead.
 */

interface HealthCheckCache {
  status: 'ok' | 'error';
  timestamp: number;
  frontendMode?: 'test' | 'live';
  backendMode?: 'test' | 'live';
  error?: string;
}

interface HealthCheckResult {
  ok: boolean;
  frontendMode: 'test' | 'live';
  backendMode?: 'test' | 'live';
  error?: string;
}

class StripeHealthChecker {
  private cache: HealthCheckCache | null = null;
  private readonly SUCCESS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
  private readonly ERROR_CACHE_TTL_MS = 30 * 1000;        // 30 seconds

  /**
   * Check Stripe configuration health with caching
   * @param publishableKey - Frontend Stripe publishable key
   * @returns Health check result with ok status and mode information
   */
  async checkHealth(publishableKey: string): Promise<HealthCheckResult> {
    // Check cache first
    const cacheTtl = this.cache?.status === 'ok' ? this.SUCCESS_CACHE_TTL_MS : this.ERROR_CACHE_TTL_MS;
    if (this.cache && Date.now() - this.cache.timestamp < cacheTtl) {
      return {
        ok: this.cache.status === 'ok',
        frontendMode: this.cache.frontendMode || 'test',
        backendMode: this.cache.backendMode,
        error: this.cache.error
      };
    }

    // Extract mode from frontend publishable key
    const frontendMode: 'test' | 'live' = publishableKey.startsWith('pk_test_') ? 'test' : 'live';

    // Call backend health check
    try {
      const { mode: backendMode, error } = await stripeApi.healthCheck();

      if (error) {
        this.cache = {
          status: 'error',
          timestamp: Date.now(),
          frontendMode,
          error: error
        };
        return { ok: false, frontendMode, error };
      }

      // Handle backend unable to determine its mode (secret not available)
      if (backendMode === 'unknown') {
        const errorMsg = 'Payment system is temporarily unavailable. Please try again in a moment.';
        this.cache = {
          status: 'error',
          timestamp: Date.now(),
          frontendMode,
          error: errorMsg
        };
        return { ok: false, frontendMode, error: errorMsg };
      }

      // Detect mismatch between frontend and backend modes
      if (backendMode && frontendMode !== backendMode) {
        const errorMsg = `Stripe configuration mismatch: Frontend is ${frontendMode} mode, backend is ${backendMode} mode`;
        this.cache = {
          status: 'error',
          timestamp: Date.now(),
          frontendMode,
          backendMode: backendMode as 'test' | 'live',
          error: errorMsg
        };
        return {
          ok: false,
          frontendMode,
          backendMode: backendMode as 'test' | 'live',
          error: errorMsg
        };
      }

      // Success - modes match
      this.cache = {
        status: 'ok',
        timestamp: Date.now(),
        frontendMode,
        backendMode: backendMode as 'test' | 'live'
      };

      return {
        ok: true,
        frontendMode,
        backendMode: backendMode as 'test' | 'live'
      };

    } catch (e: any) {
      const errorMsg = e?.message || 'Health check network error';
      this.cache = {
        status: 'error',
        timestamp: Date.now(),
        frontendMode,
        error: errorMsg
      };
      return { ok: false, frontendMode, error: errorMsg };
    }
  }

  /**
   * Validate configuration before a Stripe operation
   * Throws an error if configuration is invalid
   * @param publishableKey - Frontend Stripe publishable key
   */
  async validateBeforeOperation(publishableKey: string): Promise<void> {
    const result = await this.checkHealth(publishableKey);
    if (!result.ok) {
      throw new Error(result.error || 'Stripe configuration invalid');
    }
  }

  /**
   * Clear the health check cache
   * Use this after configuration changes or errors to force a fresh check
   */
  clearCache(): void {
    this.cache = null;
  }
}

// Export singleton instance
export const stripeHealthChecker = new StripeHealthChecker();
